<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

// // Store management imports
import { useBreakStore } from '@/stores/break';

// // Utils imports
import { wrapIndex } from '@/utils/arrayNavigation';

// // Composables
import { useAssetLoading } from '@/composables/useAssetLoading';
import { useNacxMetrics } from '@/composables/useNacxMetrics';
import { useElapsedPlaybackTime } from '@/composables/useElapsedPlaybackTime';

// // Component imports
import BarcodeQr from '@/components/BarcodeQr/BarcodeQrComp.vue';

const storeBreak = useBreakStore();

// // Asset loading
const { isAssetsLoaded, isTemplateReadyToFadeIn, waitForComponentAssets, templateFadeInHandler } =
  useAssetLoading(5000);
const containerRef = ref();

// // NACX Metrics
const { markDismissed, trackKeyPress } = useNacxMetrics();

// // Spinner (breathing dot) → "Grab your remote" intro, matching TempPollStreamOverlay's opening beat.
const SPINNER_DURATION_SECONDS = 1.5;
const INTRO_DURATION_SECONDS = 3;
const ONBOARDING_DURATION_SECONDS = SPINNER_DURATION_SECONDS + INTRO_DURATION_SECONDS;

// // null = nothing focused. Hotspots stay unfocused until the user's first key press.
const focusedProductIndex = ref(null);
const selectedProduct = ref(null);
const hasUserInteracted = ref(false);

// // Elapsed playback time pauses while the detail drawer is open so a focused hotspot can't expire mid-read.
const { elapsedSeconds } = useElapsedPlaybackTime({ isPaused: () => !!selectedProduct.value });

// // Onboarding: cycles a blue glow through each visible orb once, with the drawer peeking under it.
const GLOW_STEP_DURATION_SECONDS = 2;

let glowCycleTimeout = null;
let hasGlowCycleStarted = false;
const glowingProductIndex = ref(null);

const showSpinner = computed(() => elapsedSeconds.value < SPINNER_DURATION_SECONDS);
const showIntro = computed(
  () =>
    elapsedSeconds.value >= SPINNER_DURATION_SECONDS &&
    elapsedSeconds.value < ONBOARDING_DURATION_SECONDS,
);
const isOnboarding = computed(() => elapsedSeconds.value < ONBOARDING_DURATION_SECONDS);

// // Raw elapsed time, not shifted by onboarding — hotspot windows are authored relative to overlay start.
const hotspotElapsedSeconds = computed(() => elapsedSeconds.value);

const hotspotSecondsFor = (product) => {
  const hotspot = (storeBreak.breakHotspots || []).find(
    (candidate) => candidate.productUuid === product.productUuid,
  );
  if (!hotspot) return null;

  const isMilliseconds = hotspot.timingUnits === 'milliseconds';
  const divisor = isMilliseconds ? 1000 : 1;

  return {
    startsAt: hotspot.startsAt / divisor,
    duration: hotspot.duration / divisor,
    positionX: hotspot.positionX,
    positionY: hotspot.positionY,
  };
};

// // Labels are centered on their pin by default, but that runs a long
// // product name off-screen once the pin sits within ~1/4 screen-width of
// // an edge (e.g. position_x: 20 with a 40+ character title). Anchor to the
// // near edge instead of centering when a pin is close enough for it to matter.
const EDGE_ANCHOR_THRESHOLD_PERCENT = 25;

const labelAnchorFor = (positionX) => {
  if (positionX <= EDGE_ANCHOR_THRESHOLD_PERCENT) return 'left';
  if (positionX >= 100 - EDGE_ANCHOR_THRESHOLD_PERCENT) return 'right';
  return 'center';
};

const visibleHotspotProducts = computed(() => {
  if (isOnboarding.value) return [];

  return (storeBreak.breakProducts || [])
    .map((product) => ({ product, hotspot: hotspotSecondsFor(product) }))
    .filter(({ hotspot }) => {
      if (!hotspot) return false;
      const endsAt = hotspot.startsAt + hotspot.duration;
      return (
        hotspotElapsedSeconds.value >= hotspot.startsAt && hotspotElapsedSeconds.value < endsAt
      );
    })
    .map((entry) => ({ ...entry, labelAnchor: labelAnchorFor(entry.hotspot.positionX) }));
});

// // If the user's focused hotspot expires out from under them (its window
// // ends) drop focus back to unfocused rather than silently jumping to
// // another product — only explicit navigation should change focus.
watch(visibleHotspotProducts, (visible) => {
  if (focusedProductIndex.value === null) return;
  const stillVisible = visible.some(
    ({ product }) => product.productPosition === focusedProductIndex.value,
  );
  if (!stillVisible) focusedProductIndex.value = null;
});

// // Previews whichever product is glowing, kept separate from `selectedProduct`.
const peekingProduct = computed(() => {
  if (glowingProductIndex.value === null) return null;
  const found = visibleHotspotProducts.value.find(
    ({ product }) => product.productPosition === glowingProductIndex.value,
  );
  return found?.product ?? null;
});

const stopGlowCycle = () => {
  if (glowCycleTimeout) {
    clearTimeout(glowCycleTimeout);
    glowCycleTimeout = null;
  }
  glowingProductIndex.value = null;
};

// // Plays once: glows each visible orb in turn, then stops.
const startGlowCycle = () => {
  const visible = visibleHotspotProducts.value;
  if (!visible.length || hasUserInteracted.value) return;

  let currentIndex = 0;

  const glowNextHotspot = () => {
    if (currentIndex >= visible.length || hasUserInteracted.value) {
      glowingProductIndex.value = null;
      return;
    }

    glowingProductIndex.value = visible[currentIndex].product.productPosition;

    glowCycleTimeout = setTimeout(() => {
      currentIndex++;
      glowNextHotspot();
    }, GLOW_STEP_DURATION_SECONDS * 1000);
  };

  glowNextHotspot();
};

// // Onboarding sequence: once the first hotspot appears, auto-play the
// // glow cycle once (if the user still hasn't pressed a key).
watch(visibleHotspotProducts, (visible) => {
  if (hasGlowCycleStarted || hasUserInteracted.value || !visible.length) return;
  hasGlowCycleStarted = true;

  startGlowCycle();
});

// // TempProductHotspots handler functions

// // Real input always wins: the first keypress after onboarding aborts the glow cycle.
const registerFirstInteraction = () => {
  if (hasUserInteracted.value) return;
  hasUserInteracted.value = true;
  stopGlowCycle();
  clearSelection();
};

const keyDownHandler = (e) => {
  trackKeyPress(e);

  if (isOnboarding.value) return;

  registerFirstInteraction();

  if (selectedProduct.value) {
    if (e.key === 'Enter' || e.key === 'Escape' || e.key === 'Backspace') {
      closeProductHandler();
    }
    return;
  }

  const visible = visibleHotspotProducts.value;
  if (!visible.length) return;

  const isNavigationKey = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Enter'].includes(
    e.key,
  );
  if (!isNavigationKey) return;

  // // First navigation key with nothing focused yet: focus the first visible
  // // hotspot rather than computing a "next/previous" relative to nothing.
  if (focusedProductIndex.value === null) {
    if (e.key === 'Enter') return; // // Enter with nothing focused is a no-op.
    focusedProductIndex.value = visible[0].product.productPosition;
    return;
  }

  const currentIdx = visible.findIndex(
    ({ product }) => product.productPosition === focusedProductIndex.value,
  );

  switch (e.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      focusedProductIndex.value =
        visible[wrapIndex(currentIdx, 1, visible.length)].product.productPosition;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      focusedProductIndex.value =
        visible[wrapIndex(currentIdx, -1, visible.length)].product.productPosition;
      break;
    case 'Enter': {
      const focused = visible.find(
        ({ product }) => product.productPosition === focusedProductIndex.value,
      );
      if (focused) selectedProduct.value = focused.product;
      break;
    }
    default:
      break;
  }
};

// // Clear focus on close so the "Press ENTER to explore" label doesn't
// // immediately re-appear on the orb that was just closed for — matches the
// // blank, unfocused state right after onboarding.
const clearSelection = () => {
  selectedProduct.value = null;
  focusedProductIndex.value = null;
};

const closeProductHandler = () => {
  markDismissed();
  clearSelection();
};

// // TempProductHotspots lifecycle hooks

onMounted(async () => {
  await waitForComponentAssets(containerRef.value);
  templateFadeInHandler();

  document.addEventListener('keydown', keyDownHandler);
});

onUnmounted(() => {
  document.removeEventListener('keydown', keyDownHandler);
  stopGlowCycle();
});
</script>

<template>
  <section ref="containerRef" class="product-hotspots">
    <div
      class="product-hotspots__container"
      :class="{
        'is-clear': !isTemplateReadyToFadeIn || !isAssetsLoaded,
        'is-opaque': isTemplateReadyToFadeIn && isAssetsLoaded,
      }"
    >
      <div v-if="showSpinner" class="product-hotspots__spinner"></div>

      <div
        class="product-hotspots__intro"
        :class="{
          'product-hotspots__intro--open': showIntro,
          'product-hotspots__intro--closed': !showIntro,
        }"
      >
        <div class="product-hotspots__orb p1"></div>
        <div class="product-hotspots__orb p2"></div>
        <div class="product-hotspots__orb p3"></div>

        <span class="product-hotspots__intro-copy">Grab your remote</span>
      </div>

      <TransitionGroup tag="div" name="hotspot-fade" class="product-hotspots__pins">
        <div
          v-for="{ product, hotspot, labelAnchor } in visibleHotspotProducts"
          :key="product.productUuid"
          class="product-hotspots__pin"
          :class="{
            'product-hotspots__pin--focused': focusedProductIndex === product.productPosition,
            'product-hotspots__pin--glowing': glowingProductIndex === product.productPosition,
          }"
          :style="{ left: `${hotspot.positionX}%`, top: `${hotspot.positionY}%` }"
        >
          <div class="product-hotspots__dot-wrap">
            <div class="product-hotspots__dot"></div>
          </div>

          <span
            v-if="focusedProductIndex === product.productPosition"
            class="product-hotspots__label"
            :class="`product-hotspots__label--anchor-${labelAnchor}`"
          >
            {{ product.productName }}
          </span>
        </div>
      </TransitionGroup>

      <Transition name="hotspot-detail-slide">
        <div
          v-if="selectedProduct || peekingProduct"
          class="product-hotspots__detail"
          :class="{ 'product-hotspots__detail--peek': !selectedProduct && peekingProduct }"
        >
          <button
            v-if="selectedProduct"
            class="product-hotspots__close"
            @click="closeProductHandler"
          >
            ✕
          </button>

          <img
            v-if="(selectedProduct || peekingProduct).productImages[0]?.productImageSrc"
            class="product-hotspots__image"
            :src="(selectedProduct || peekingProduct).productImages[0]?.productImageSrc"
          />

          <div class="product-hotspots__info">
            <span class="product-hotspots__name">{{
              (selectedProduct || peekingProduct).productName
            }}</span>

            <span v-if="selectedProduct" class="product-hotspots__dismiss-hint">
              Press Enter to close
            </span>
          </div>

          <BarcodeQr
            v-if="selectedProduct"
            class="product-hotspots__qr"
            :target-url="selectedProduct.productUrl"
            :interactive-break-uuid="storeBreak.breakUuid"
            :product-uuid="selectedProduct.productUuid"
            :ad-id="storeBreak.breakGeneratedIdentifier"
          />
        </div>
      </Transition>
    </div>
  </section>
</template>
